import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3002/api').replace('/api', '');

interface League {
  id: string;
  name: string;
  abbreviation: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  facebookUrl: string | null;
  discordUrl: string | null;
  whatsappUrl: string | null;
  isActive: boolean;
  displayOrder: number;
}

interface Highlight {
  id: string;
  type: '180' | 'high_checkout' | 'best_leg' | 'ton_plus' | '9_darter' | 'other';
  value: number;
  description: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  achievedAt: string | null;
  platform: string | null;
  isVerified: boolean;
  displayOrder: number;
  createdAt: string;
}

export default function MyProfile() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRegeneratingBanner, setIsRegeneratingBanner] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile fields
  const [profileSlug, setProfileSlug] = useState('');
  const [bio, setBio] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [profileLayout, setProfileLayout] = useState<'single' | 'tabbed'>('single');
  const [showRealName, setShowRealName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [realName, setRealName] = useState('');
  const [nickname, setNickname] = useState('');
  const [statAverage, setStatAverage] = useState('');
  const [stat180s, setStat180s] = useState('');
  const [statBestLeg, setStatBestLeg] = useState('');
  const [statHighestCheckout, setStatHighestCheckout] = useState('');
  const [dartsBrand, setDartsBrand] = useState('');
  const [dartsWeight, setDartsWeight] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialDiscord, setSocialDiscord] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialTwitter, setSocialTwitter] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [dartcounterUrl, setDartcounterUrl] = useState('');

  // Online darts platforms - Webcam/Manual
  const [platformDartcounter, setPlatformDartcounter] = useState('');
  const [platformWda, setPlatformWda] = useState('');
  const [platformLidarts, setPlatformLidarts] = useState('');
  const [platformNakka, setPlatformNakka] = useState('');
  const [platformProdarter, setPlatformProdarter] = useState('');
  const [platformDartconnect, setPlatformDartconnect] = useState('');
  const [platformGodartspro, setPlatformGodartspro] = useState('');
  // Auto-Scoring Systems (boolean - has this system)
  const [hasScolia, setHasScolia] = useState(false);
  const [hasAutodarts, setHasAutodarts] = useState(false);
  const [hasTargetomni, setHasTargetomni] = useState(false);

  // Leagues
  const [leagues, setLeagues] = useState<League[]>([]);
  const [showAddLeague, setShowAddLeague] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueAbbr, setNewLeagueAbbr] = useState('');

  // Highlights
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [highlightLimit, setHighlightLimit] = useState(2);
  const [highlightRemaining, setHighlightRemaining] = useState(2);
  const [showAddHighlight, setShowAddHighlight] = useState(false);
  const [isAddingHighlight, setIsAddingHighlight] = useState(false);
  const highlightImageRef = useRef<HTMLInputElement>(null);
  const [newHighlight, setNewHighlight] = useState({
    type: '180' as Highlight['type'],
    value: '',
    description: '',
    videoUrl: '',
    achievedAt: '',
    platform: ''
  });
  const [newHighlightImage, setNewHighlightImage] = useState<File | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/profile');
        const { profile, leagues: userLeagues } = response.data;

        setProfileSlug(profile.profileSlug || '');
        setBio(profile.bio || '');
        setIsPublic(profile.isPublic || false);
        setProfileLayout(profile.layout || 'single');
        setShowRealName(profile.showRealName || false);
        setDisplayName(profile.displayName || '');
        setRealName(profile.realName || '');
        setNickname(profile.nickname || '');
        setProfileImage(profile.profileImage || null);
        setBannerUrl(profile.bannerUrl || null);
        setStatAverage(profile.stats.average?.toString() || '');
        setStat180s(profile.stats.oneEighties?.toString() || '');
        setStatBestLeg(profile.stats.bestLeg?.toString() || '');
        setStatHighestCheckout(profile.stats.highestCheckout?.toString() || '');
        setDartsBrand(profile.equipment.dartsBrand || '');
        setDartsWeight(profile.equipment.dartsWeight || '');
        setSocialFacebook(profile.social.facebook || '');
        setSocialDiscord(profile.social.discord || '');
        setSocialTiktok(profile.social.tiktok || '');
        setSocialYoutube(profile.social.youtube || '');
        setSocialTwitter(profile.social.twitter || '');
        setSocialInstagram(profile.social.instagram || '');
        setDartcounterUrl(profile.dartcounterUrl || '');
        // Load platforms
        setPlatformDartcounter(profile.platforms?.webcam?.dartcounter || '');
        setPlatformWda(profile.platforms?.webcam?.wda || '');
        setPlatformLidarts(profile.platforms?.webcam?.lidarts || '');
        setPlatformNakka(profile.platforms?.webcam?.nakka || '');
        setPlatformProdarter(profile.platforms?.webcam?.prodarter || '');
        setPlatformDartconnect(profile.platforms?.webcam?.dartconnect || '');
        setPlatformGodartspro(profile.platforms?.webcam?.godartspro || '');
        setHasScolia(profile.platforms?.autoScoring?.scolia || false);
        setHasAutodarts(profile.platforms?.autoScoring?.autodarts || false);
        setHasTargetomni(profile.platforms?.autoScoring?.targetomni || false);
        setLeagues(userLeagues || []);

        // Fetch highlights
        const highlightsResponse = await api.get('/highlights');
        setHighlights(highlightsResponse.data.highlights || []);
        setHighlightLimit(highlightsResponse.data.limit || 2);
        setHighlightRemaining(highlightsResponse.data.remaining || 0);
      } catch {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/profile', {
        profileSlug: profileSlug || undefined,
        bio: bio || undefined,
        isPublic,
        profileLayout,
        statAverage: statAverage ? parseFloat(statAverage) : undefined,
        stat180s: stat180s ? parseInt(stat180s) : undefined,
        statBestLeg: statBestLeg ? parseInt(statBestLeg) : undefined,
        statHighestCheckout: statHighestCheckout ? parseInt(statHighestCheckout) : undefined,
        dartsBrand: dartsBrand || undefined,
        dartsWeight: dartsWeight || undefined,
        socialFacebook: socialFacebook || undefined,
        socialDiscord: socialDiscord || undefined,
        socialTiktok: socialTiktok || undefined,
        socialYoutube: socialYoutube || undefined,
        socialTwitter: socialTwitter || undefined,
        socialInstagram: socialInstagram || undefined,
        dartcounterUrl: dartcounterUrl || undefined,
        platformDartcounter: platformDartcounter || undefined,
        platformWda: platformWda || undefined,
        platformLidarts: platformLidarts || undefined,
        platformNakka: platformNakka || undefined,
        platformProdarter: platformProdarter || undefined,
        platformDartconnect: platformDartconnect || undefined,
        platformGodartspro: platformGodartspro || undefined,
        hasScolia,
        hasAutodarts,
        hasTargetomni,
        showRealName
      });
      setSuccess('Profile saved successfully');
      // Jump to top to show success message
      window.scrollTo(0, 0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save profile');
      // Jump to top to show error message
      window.scrollTo(0, 0);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLeagueName.trim()) return;

    try {
      const response = await api.post('/leagues', {
        name: newLeagueName.trim(),
        abbreviation: newLeagueAbbr.trim() || undefined
      });
      setLeagues([...leagues, response.data.league]);
      setNewLeagueName('');
      setNewLeagueAbbr('');
      setShowAddLeague(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to add league');
    }
  };

  const handleDeleteLeague = async (id: string) => {
    if (!confirm('Delete this league?')) return;

    try {
      await api.delete(`/leagues/${id}`);
      setLeagues(leagues.filter(l => l.id !== id));
    } catch {
      setError('Failed to delete league');
    }
  };

  const handleToggleLeagueActive = async (league: League) => {
    try {
      await api.put(`/leagues/${league.id}`, { isActive: !league.isActive });
      setLeagues(leagues.map(l => l.id === league.id ? { ...l, isActive: !l.isActive } : l));
    } catch {
      setError('Failed to update league');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/upload/profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfileImage(response.data.imageUrl);
      setSuccess('Profile image uploaded');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDelete = async () => {
    if (!confirm('Remove your profile image?')) return;

    try {
      await api.delete('/upload/profile-image');
      setProfileImage(null);
      setSuccess('Profile image removed');
    } catch {
      setError('Failed to remove image');
    }
  };

  const handleRegenerateBanner = async () => {
    setIsRegeneratingBanner(true);
    setError('');

    try {
      const response = await api.post('/profile/regenerate-banner');
      setBannerUrl(response.data.bannerUrl);
      setSuccess('Banner regenerated successfully');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to regenerate banner');
    } finally {
      setIsRegeneratingBanner(false);
    }
  };

  const handleAddHighlight = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newHighlight.value) return;

    setIsAddingHighlight(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('type', newHighlight.type);
      formData.append('value', newHighlight.value);
      if (newHighlight.description) formData.append('description', newHighlight.description);
      if (newHighlight.videoUrl) formData.append('videoUrl', newHighlight.videoUrl);
      if (newHighlight.achievedAt) formData.append('achievedAt', newHighlight.achievedAt);
      if (newHighlight.platform) formData.append('platform', newHighlight.platform);
      if (newHighlightImage) formData.append('image', newHighlightImage);

      const response = await api.post('/highlights', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setHighlights([...highlights, response.data.highlight]);
      setHighlightRemaining(response.data.remaining);
      setNewHighlight({
        type: '180',
        value: '',
        description: '',
        videoUrl: '',
        achievedAt: '',
        platform: ''
      });
      setNewHighlightImage(null);
      setShowAddHighlight(false);
      setSuccess('Highlight added');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to add highlight');
    } finally {
      setIsAddingHighlight(false);
    }
  };

  const handleDeleteHighlight = async (id: string) => {
    if (!confirm('Delete this highlight?')) return;

    try {
      await api.delete(`/highlights/${id}`);
      setHighlights(highlights.filter(h => h.id !== id));
      setHighlightRemaining(prev => prev + 1);
      setSuccess('Highlight deleted');
    } catch {
      setError('Failed to delete highlight');
    }
  };

  const getHighlightTypeLabel = (type: Highlight['type']) => {
    const labels: Record<Highlight['type'], string> = {
      '180': '180',
      'high_checkout': 'High Checkout',
      'best_leg': 'Best Leg',
      'ton_plus': '100+ Score',
      '9_darter': '9 Darter',
      'other': 'Other'
    };
    return labels[type];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const profileUrl = profileSlug ? `${window.location.origin}/p/${profileSlug}` : null;

  return (
    <>
      {/* Full-screen saving overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-50 bg-[#0a0a18] flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="text-white mt-4">Saving profile...</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title mb-0">My Public Profile</h1>
        {profileUrl && (
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm"
          >
            View Profile
          </a>
        )}
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 text-green-300 px-4 py-3 rounded-lg text-sm border border-green-800">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Profile Image */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Profile Picture</h2>
          <div className="flex items-center gap-6">
            <div className="relative">
              {profileImage ? (
                <img
                  src={`${API_BASE}${profileImage}`}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-[#1111d4]"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#252550] border-4 border-[#2a2a4a] flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
                id="profile-image-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="btn-primary text-sm"
              >
                {isUploading ? 'Uploading...' : profileImage ? 'Change Photo' : 'Upload Photo'}
              </button>
              {profileImage && (
                <button
                  type="button"
                  onClick={handleImageDelete}
                  className="btn-secondary text-sm text-red-400"
                >
                  Remove
                </button>
              )}
              <p className="text-xs text-gray-500">JPEG, PNG, WebP or GIF. Max 5MB.</p>
            </div>
          </div>
        </div>

        {/* Profile Banner */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Profile Banner</h2>
          <p className="text-sm text-gray-400 mb-4">
            Your personalized stone-textured banner is automatically generated with your display name.
          </p>
          {bannerUrl ? (
            <div className="bg-[#0a0a18] p-4 rounded-lg">
              <img
                src={bannerUrl.startsWith('http') ? bannerUrl : `${API_BASE}${bannerUrl}`}
                alt="Profile Banner"
                className="w-full h-auto rounded-lg"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#252550] p-8 rounded-lg text-center">
                <p className="text-gray-400 mb-4">No banner generated yet</p>
                <button
                  type="button"
                  onClick={handleRegenerateBanner}
                  disabled={isRegeneratingBanner}
                  className="btn-primary"
                >
                  {isRegeneratingBanner ? 'Generating...' : 'Generate Banner'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Profile URL & Visibility */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Profile Settings</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Profile URL</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{window.location.origin}/p/</span>
                <input
                  type="text"
                  value={profileSlug}
                  onChange={(e) => setProfileSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="input flex-1"
                  placeholder="your-name"
                  maxLength={50}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Only lowercase letters, numbers, and hyphens</p>
            </div>

            <label className="flex items-center gap-3 text-gray-300">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
              />
              <span>Make profile public</span>
            </label>

            <div className="border-t border-[#2a2a4a] pt-4">
              <label className="flex items-center gap-3 text-gray-300">
                <input
                  type="checkbox"
                  checked={showRealName}
                  onChange={(e) => setShowRealName(e.target.checked)}
                  className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                />
                <div>
                  <span>Show both names on public profile</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {displayName && (realName || nickname) ? (
                      <>Your profile shows <span className="text-white">{displayName}</span>. Enable to also show <span className="text-white">{realName && nickname ? (displayName === realName ? nickname : realName) : ''}</span></>
                    ) : (
                      'Display your alternate name (nickname or real name) below your main display name'
                    )}
                  </p>
                </div>
              </label>
            </div>

            <div>
              <label className="label">Profile Layout</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    name="layout"
                    value="single"
                    checked={profileLayout === 'single'}
                    onChange={() => setProfileLayout('single')}
                    className="text-[#1111d4]"
                  />
                  <span>Single Page</span>
                  <span className="text-xs text-gray-500">(all sections visible)</span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    name="layout"
                    value="tabbed"
                    checked={profileLayout === 'tabbed'}
                    onChange={() => setProfileLayout('tabbed')}
                    className="text-[#1111d4]"
                  />
                  <span>Tabbed</span>
                  <span className="text-xs text-gray-500">(click to switch sections)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">About</h2>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="input"
            rows={4}
            placeholder="Tell people about yourself..."
            maxLength={1000}
          />
        </div>

        {/* Stats */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Performance Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="label">Average</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="200"
                value={statAverage}
                onChange={(e) => setStatAverage(e.target.value)}
                className="input"
                placeholder="e.g., 65.5"
              />
            </div>
            <div>
              <label className="label">180s</label>
              <input
                type="number"
                min="0"
                value={stat180s}
                onChange={(e) => setStat180s(e.target.value)}
                className="input"
                placeholder="e.g., 12"
              />
            </div>
            <div>
              <label className="label">Best Leg (darts)</label>
              <input
                type="number"
                min="9"
                value={statBestLeg}
                onChange={(e) => setStatBestLeg(e.target.value)}
                className="input"
                placeholder="e.g., 15"
              />
            </div>
            <div>
              <label className="label">Highest Checkout</label>
              <input
                type="number"
                min="2"
                max="170"
                value={statHighestCheckout}
                onChange={(e) => setStatHighestCheckout(e.target.value)}
                className="input"
                placeholder="e.g., 170"
              />
            </div>
          </div>
        </div>

        {/* Equipment */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Equipment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Darts Brand</label>
              <input
                type="text"
                value={dartsBrand}
                onChange={(e) => setDartsBrand(e.target.value)}
                className="input"
                placeholder="e.g., Target, Winmau, Unicorn"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">Darts Weight</label>
              <input
                type="text"
                value={dartsWeight}
                onChange={(e) => setDartsWeight(e.target.value)}
                className="input"
                placeholder="e.g., 22g"
                maxLength={50}
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Social Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Facebook URL</label>
              <input
                type="url"
                value={socialFacebook}
                onChange={(e) => setSocialFacebook(e.target.value)}
                className="input"
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <label className="label">Discord Username</label>
              <input
                type="text"
                value={socialDiscord}
                onChange={(e) => setSocialDiscord(e.target.value)}
                className="input"
                placeholder="username#1234"
              />
            </div>
            <div>
              <label className="label">TikTok URL</label>
              <input
                type="url"
                value={socialTiktok}
                onChange={(e) => setSocialTiktok(e.target.value)}
                className="input"
                placeholder="https://tiktok.com/@..."
              />
            </div>
            <div>
              <label className="label">YouTube URL</label>
              <input
                type="url"
                value={socialYoutube}
                onChange={(e) => setSocialYoutube(e.target.value)}
                className="input"
                placeholder="https://youtube.com/@..."
              />
            </div>
            <div>
              <label className="label">Twitter/X URL</label>
              <input
                type="url"
                value={socialTwitter}
                onChange={(e) => setSocialTwitter(e.target.value)}
                className="input"
                placeholder="https://twitter.com/..."
              />
            </div>
            <div>
              <label className="label">Instagram URL</label>
              <input
                type="url"
                value={socialInstagram}
                onChange={(e) => setSocialInstagram(e.target.value)}
                className="input"
                placeholder="https://instagram.com/..."
              />
            </div>
          </div>
        </div>

        {/* Online Darts Platforms */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-2">Online Darts Platforms</h2>
          <p className="text-sm text-gray-400 mb-4">Add your usernames so others can find and challenge you</p>

          {/* Webcam/Manual Scoring */}
          <h3 className="text-md font-medium text-gray-300 mb-3 mt-4">Webcam / Manual Scoring</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">DartCounter</label>
              <input
                type="url"
                value={platformDartcounter}
                onChange={(e) => setPlatformDartcounter(e.target.value)}
                className="input"
                placeholder="https://www.dartcounter.com/profile/..."
                maxLength={255}
              />
            </div>
            <div>
              <label className="label">WDA (Webcam Darts Association)</label>
              <input
                type="text"
                value={platformWda}
                onChange={(e) => setPlatformWda(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">Lidarts</label>
              <input
                type="text"
                value={platformLidarts}
                onChange={(e) => setPlatformLidarts(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">N01 (Nakka)</label>
              <input
                type="text"
                value={platformNakka}
                onChange={(e) => setPlatformNakka(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">Pro-Darter</label>
              <input
                type="text"
                value={platformProdarter}
                onChange={(e) => setPlatformProdarter(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">DartConnect</label>
              <input
                type="text"
                value={platformDartconnect}
                onChange={(e) => setPlatformDartconnect(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
            <div>
              <label className="label">GoDartsPro</label>
              <input
                type="text"
                value={platformGodartspro}
                onChange={(e) => setPlatformGodartspro(e.target.value)}
                className="input"
                placeholder="Username"
                maxLength={100}
              />
            </div>
          </div>

          {/* Auto-Scoring Systems */}
          <h3 className="text-md font-medium text-gray-300 mb-3 mt-6">Auto-Scoring Systems</h3>
          <p className="text-sm text-gray-500 mb-3">Select the systems you have set up</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer p-3 bg-[#252550] rounded-lg hover:bg-[#2a2a5a] transition-colors">
              <input
                type="checkbox"
                checked={hasScolia}
                onChange={(e) => setHasScolia(e.target.checked)}
                className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5 text-[#1111d4]"
              />
              <span>Scolia</span>
            </label>
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer p-3 bg-[#252550] rounded-lg hover:bg-[#2a2a5a] transition-colors">
              <input
                type="checkbox"
                checked={hasAutodarts}
                onChange={(e) => setHasAutodarts(e.target.checked)}
                className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5 text-[#1111d4]"
              />
              <span>Autodarts</span>
            </label>
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer p-3 bg-[#252550] rounded-lg hover:bg-[#2a2a5a] transition-colors">
              <input
                type="checkbox"
                checked={hasTargetomni}
                onChange={(e) => setHasTargetomni(e.target.checked)}
                className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5 text-[#1111d4]"
              />
              <span>Target Omni / DartCounter</span>
            </label>
          </div>
        </div>

        <button type="submit" disabled={isSaving} className="btn-primary">
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>

      {/* Leagues Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">My Leagues</h2>
          <button
            type="button"
            onClick={() => setShowAddLeague(true)}
            className="btn-primary text-sm"
          >
            Add League
          </button>
        </div>

        {showAddLeague && (
          <form onSubmit={handleAddLeague} className="mb-4 p-4 bg-[#252550] rounded-lg">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="label">League Name *</label>
                <input
                  type="text"
                  value={newLeagueName}
                  onChange={(e) => setNewLeagueName(e.target.value)}
                  className="input"
                  required
                  maxLength={100}
                />
              </div>
              <div>
                <label className="label">Abbreviation</label>
                <input
                  type="text"
                  value={newLeagueAbbr}
                  onChange={(e) => setNewLeagueAbbr(e.target.value)}
                  className="input"
                  maxLength={20}
                  placeholder="e.g., PDL"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">Add</button>
              <button
                type="button"
                onClick={() => setShowAddLeague(false)}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {leagues.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No leagues added yet</p>
        ) : (
          <div className="space-y-3">
            {leagues.map((league) => (
              <div key={league.id} className="flex items-center justify-between p-3 bg-[#252550] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1a1a3a] rounded flex items-center justify-center text-[#1111d4] font-bold text-sm">
                    {league.abbreviation || league.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-white">{league.name}</p>
                    {league.abbreviation && (
                      <p className="text-sm text-gray-400">{league.abbreviation}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleLeagueActive(league)}
                    className={`px-3 py-1 rounded text-xs ${
                      league.isActive
                        ? 'bg-green-900/30 text-green-300 border border-green-800/50'
                        : 'bg-gray-800/30 text-gray-400 border border-gray-700/50'
                    }`}
                  >
                    {league.isActive ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteLeague(league.id)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Highlights Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Top Scores & Finishes</h2>
            <p className="text-sm text-gray-400">
              {highlights.length} of {highlightLimit} highlights used
              {highlightRemaining === 0 && (
                <span className="text-yellow-400 ml-2">(Upgrade to Pro for more!)</span>
              )}
            </p>
          </div>
          {highlightRemaining > 0 && (
            <button
              type="button"
              onClick={() => setShowAddHighlight(true)}
              className="btn-primary text-sm"
            >
              Add Highlight
            </button>
          )}
        </div>

        {showAddHighlight && (
          <form onSubmit={handleAddHighlight} className="mb-4 p-4 bg-[#252550] rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label">Type *</label>
                <select
                  value={newHighlight.type}
                  onChange={(e) => setNewHighlight({ ...newHighlight, type: e.target.value as Highlight['type'] })}
                  className="input"
                >
                  <option value="180">180</option>
                  <option value="high_checkout">High Checkout</option>
                  <option value="best_leg">Best Leg (darts)</option>
                  <option value="ton_plus">100+ Score</option>
                  <option value="9_darter">9 Darter</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Value *</label>
                <input
                  type="number"
                  value={newHighlight.value}
                  onChange={(e) => setNewHighlight({ ...newHighlight, value: e.target.value })}
                  className="input"
                  placeholder={newHighlight.type === 'best_leg' ? 'e.g., 12' : 'e.g., 170'}
                  min="1"
                  max="999"
                  required
                />
              </div>
              <div>
                <label className="label">Platform</label>
                <input
                  type="text"
                  value={newHighlight.platform}
                  onChange={(e) => setNewHighlight({ ...newHighlight, platform: e.target.value })}
                  className="input"
                  placeholder="e.g., DartCounter, Lidarts"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="label">Date Achieved</label>
                <input
                  type="date"
                  value={newHighlight.achievedAt}
                  onChange={(e) => setNewHighlight({ ...newHighlight, achievedAt: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Description</label>
                <input
                  type="text"
                  value={newHighlight.description}
                  onChange={(e) => setNewHighlight({ ...newHighlight, description: e.target.value })}
                  className="input"
                  placeholder="Optional note about this achievement"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="label">Video URL</label>
                <input
                  type="url"
                  value={newHighlight.videoUrl}
                  onChange={(e) => setNewHighlight({ ...newHighlight, videoUrl: e.target.value })}
                  className="input"
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div>
                <label className="label">Proof Image</label>
                <input
                  ref={highlightImageRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => setNewHighlightImage(e.target.files?.[0] || null)}
                  className="input text-sm file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-[#1111d4] file:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Screenshot or photo (max 5MB)</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={isAddingHighlight} className="btn-primary text-sm">
                {isAddingHighlight ? 'Adding...' : 'Add Highlight'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddHighlight(false);
                  setNewHighlightImage(null);
                  if (highlightImageRef.current) highlightImageRef.current.value = '';
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {highlights.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#252550] rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <p className="text-gray-500">No highlights yet</p>
            <p className="text-sm text-gray-600 mt-1">Add your best 180s, checkouts, and other achievements!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {highlights.map((highlight) => (
              <div key={highlight.id} className="bg-[#252550] rounded-lg overflow-hidden">
                {highlight.imageUrl && (
                  <img
                    src={`${API_BASE}${highlight.imageUrl}`}
                    alt={getHighlightTypeLabel(highlight.type)}
                    className="w-full h-40 object-cover"
                  />
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="inline-block px-2 py-0.5 bg-[#1111d4]/20 text-[#1111d4] rounded text-xs font-medium mb-1">
                        {getHighlightTypeLabel(highlight.type)}
                      </span>
                      <p className="text-2xl font-bold text-white">{highlight.value}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteHighlight(highlight.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  {highlight.description && (
                    <p className="text-sm text-gray-400 mb-2">{highlight.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {highlight.platform && (
                      <span className="bg-[#1a1a3a] px-2 py-1 rounded">{highlight.platform}</span>
                    )}
                    {highlight.achievedAt && (
                      <span className="bg-[#1a1a3a] px-2 py-1 rounded">
                        {new Date(highlight.achievedAt).toLocaleDateString()}
                      </span>
                    )}
                    {highlight.videoUrl && (
                      <a
                        href={highlight.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#1a1a3a] px-2 py-1 rounded text-[#1111d4] hover:underline"
                      >
                        Watch Video
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
